import React, { useEffect, useMemo, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { api, formatApiErrorDetail } from "../lib/api";
import { Plus, Trash2, FileText, Download } from "lucide-react";
import { toast } from "sonner";

function formatEUR(value) {
    const n = Number(value || 0);
    try {
        return n.toLocaleString("pt-PT", {
            style: "currency",
            currency: "EUR",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    } catch {
        return `${n.toFixed(2)} €`;
    }
}

export default function WorkOrderDialog({ open, onClose, servico, motoLabel, clienteNome, onSaved }) {
    const [items, setItems] = useState([]);
    const [saving, setSaving] = useState(false);
    const [ivaTaxa, setIvaTaxa] = useState(0);

    useEffect(() => {
        if (open && servico) {
            const existing = Array.isArray(servico.work_order_items) ? servico.work_order_items : [];
            setItems(
                existing.length > 0
                    ? existing.map((i) => ({
                          descricao: i.descricao || "",
                          quantidade: i.quantidade ?? 1,
                          preco_unitario: i.preco_unitario ?? 0,
                      }))
                    : [{ descricao: "", quantidade: 1, preco_unitario: 0 }]
            );
            // Fetch current IVA from settings
            api
                .get("/public/settings")
                .then((res) => setIvaTaxa(Number(res.data?.iva_taxa) || 0))
                .catch(() => setIvaTaxa(0));
        }
    }, [open, servico]);

    const subtotal = useMemo(
        () =>
            items.reduce(
                (sum, it) => sum + Number(it.quantidade || 0) * Number(it.preco_unitario || 0),
                0
            ),
        [items]
    );
    const ivaValue = subtotal * (ivaTaxa / 100);
    const total = subtotal + ivaValue;

    function addRow() {
        setItems([...items, { descricao: "", quantidade: 1, preco_unitario: 0 }]);
    }
    function removeRow(idx) {
        setItems(items.filter((_, i) => i !== idx));
    }
    function updateRow(idx, field, value) {
        const copy = [...items];
        copy[idx] = { ...copy[idx], [field]: value };
        setItems(copy);
    }

    async function save() {
        // strip empty rows (no descricao AND no values)
        const clean = items
            .map((it) => ({
                descricao: (it.descricao || "").trim(),
                quantidade: Number(it.quantidade || 0),
                preco_unitario: Number(it.preco_unitario || 0),
            }))
            .filter(
                (it) => it.descricao !== "" || it.quantidade > 0 || it.preco_unitario > 0
            );
        // require descricao on non-empty rows
        const invalid = clean.find((it) => !it.descricao);
        if (invalid) {
            toast.error("Cada linha precisa de uma descrição.");
            return;
        }
        setSaving(true);
        try {
            await api.put(`/servicos/${servico.id}`, { work_order_items: clean });
            toast.success("Folha de obra guardada.");
            onSaved && onSaved(clean);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro ao guardar");
        } finally {
            setSaving(false);
        }
    }

    async function downloadPdf() {
        try {
            const res = await api.get(`/servicos/${servico.id}/work-order/pdf`, {
                responseType: "blob",
            });
            const blob = new Blob([res.data], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `FolhaObra-${servico.id.slice(0, 8)}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
            toast.success("PDF descarregado.");
        } catch (e) {
            toast.error("Não foi possível gerar o PDF.");
        }
    }

    const hasSavedItems =
        Array.isArray(servico?.work_order_items) && servico.work_order_items.length > 0;

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent
                className="max-w-3xl rounded-sm"
                data-testid="work-order-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 font-['Outfit']">
                        <FileText className="h-5 w-5 text-zinc-600" />
                        Folha de Obra
                        {servico?.numero ? (
                            <span className="ml-1 rounded-sm bg-zinc-900 px-2 py-0.5 font-mono text-xs font-semibold text-white">
                                Nº {servico.numero}
                            </span>
                        ) : null}
                    </DialogTitle>
                </DialogHeader>

                <div className="-mt-2 mb-3 text-xs text-zinc-600">
                    <div className="font-medium text-zinc-900">{motoLabel || "Moto"}</div>
                    <div>{clienteNome || ""} · {servico?.tipo || ""}</div>
                </div>

                <div className="border border-zinc-200">
                    <div className="grid grid-cols-12 gap-2 border-b border-zinc-200 bg-zinc-50 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-zinc-500">
                        <div className="col-span-6">Descrição</div>
                        <div className="col-span-2 text-right">Qtd</div>
                        <div className="col-span-2 text-right">Preço unitário</div>
                        <div className="col-span-1 text-right">Subtotal</div>
                        <div className="col-span-1" />
                    </div>

                    {items.length === 0 ? (
                        <div className="px-3 py-6 text-center text-sm text-zinc-500">
                            Sem linhas. Clique em "Adicionar linha" abaixo.
                        </div>
                    ) : (
                        items.map((it, idx) => {
                            const subtotal =
                                Number(it.quantidade || 0) * Number(it.preco_unitario || 0);
                            return (
                                <div
                                    key={idx}
                                    className="grid grid-cols-12 items-center gap-2 border-b border-zinc-100 px-3 py-2"
                                    data-testid={`wo-row-${idx}`}
                                >
                                    <div className="col-span-6">
                                        <Input
                                            value={it.descricao}
                                            onChange={(e) => updateRow(idx, "descricao", e.target.value)}
                                            placeholder="Ex: Substituição de pastilhas dianteiras"
                                            className="h-9 rounded-sm"
                                            data-testid={`wo-descricao-${idx}`}
                                        />
                                    </div>
                                    <div className="col-span-2">
                                        <Input
                                            type="number"
                                            min="0"
                                            step="0.5"
                                            value={it.quantidade}
                                            onChange={(e) => updateRow(idx, "quantidade", e.target.value)}
                                            className="h-9 rounded-sm text-right font-mono"
                                            data-testid={`wo-quantidade-${idx}`}
                                        />
                                    </div>
                                    <div className="col-span-2">
                                        <Input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={it.preco_unitario}
                                            onChange={(e) => updateRow(idx, "preco_unitario", e.target.value)}
                                            className="h-9 rounded-sm text-right font-mono"
                                            data-testid={`wo-preco-${idx}`}
                                        />
                                    </div>
                                    <div className="col-span-1 text-right font-mono text-sm text-zinc-700">
                                        {formatEUR(subtotal)}
                                    </div>
                                    <div className="col-span-1 flex justify-end">
                                        <Button
                                            size="icon"
                                            variant="outline"
                                            className="h-8 w-8 rounded-sm text-red-600 hover:text-red-700"
                                            onClick={() => removeRow(idx)}
                                            data-testid={`wo-remove-${idx}`}
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </Button>
                                    </div>
                                </div>
                            );
                        })
                    )}

                    <div className="flex items-center justify-between border-t border-zinc-200 bg-zinc-50 px-3 py-3">
                        <Button
                            variant="outline"
                            size="sm"
                            className="rounded-sm"
                            onClick={addRow}
                            data-testid="wo-add-row-btn"
                        >
                            <Plus className="mr-1.5 h-4 w-4" />
                            Adicionar linha
                        </Button>
                        <div className="text-right">
                            <div className="flex items-center gap-3 text-xs text-zinc-600">
                                <span className="font-semibold uppercase tracking-[0.14em] text-zinc-500">
                                    Subtotal (c/ IVA)
                                </span>
                                <span className="font-mono" data-testid="wo-subtotal">
                                    {formatEUR(subtotal)}
                                </span>
                            </div>
                            <div className="mt-1 flex items-center gap-3 text-xs text-zinc-600">
                                <span className="font-semibold uppercase tracking-[0.14em] text-zinc-500">
                                    IVA {ivaTaxa}% incluído
                                </span>
                                <span className="font-mono" data-testid="wo-iva">
                                    {formatEUR(ivaValue)}
                                </span>
                            </div>
                            <div className="mt-2 flex items-center gap-3 border-t border-zinc-300 pt-2">
                                <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-zinc-700">
                                    Total
                                </span>
                                <span
                                    className="font-['Outfit'] text-xl font-semibold text-zinc-900"
                                    data-testid="wo-total"
                                >
                                    {formatEUR(total)}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <DialogFooter className="gap-2">
                    <Button
                        variant="outline"
                        className="rounded-sm"
                        onClick={downloadPdf}
                        disabled={!hasSavedItems}
                        title={hasSavedItems ? "Descarregar PDF" : "Guarde a folha de obra para gerar PDF"}
                        data-testid="wo-pdf-btn"
                    >
                        <Download className="mr-2 h-4 w-4" />
                        Descarregar PDF
                    </Button>
                    <div className="flex-1" />
                    <Button variant="outline" className="rounded-sm" onClick={onClose}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={save}
                        disabled={saving}
                        className="rounded-sm bg-[#FF3B30] hover:bg-[#D63026]"
                        data-testid="wo-save-btn"
                    >
                        {saving ? "A guardar..." : "Guardar folha de obra"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
